import Card from "@/components/Card";
import Combobox from "@/components/Combobox";
import FormHeader from "@/components/FormHeader";
import ImageControl from "@/components/ImageControll";
import InputError from "@/components/InputError";
import Modal from "@/components/Modal";
import SkeletonCard from "@/components/SkeletonCard";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/axios";
import { formatRupiah } from "@/lib/utils";
import {
  getListSumberAir,
  getListTeganganListrikAset,
} from "@/services/getData";
import { DataAset, SumberAir, TeganganListrikAset } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  Droplets,
  FileText,
  Home,
  Loader2,
  XCircle,
  Zap,
} from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { GiBackwardTime } from "react-icons/gi";
import { MdModeEdit } from "react-icons/md";

// --- Types ---
type ValidationState = {
  id_data_aset?: string;
  id_ref_aset_luas_tanah: string;
  aset_luas_tanah: string | number;
  aset_luas_bangunan: string | number;
  id_ref_aset_tegangan_listrik: string;
  id_ref_aset_pembayaran_air: string;
  id_ref_aset_sumber_air: string;
  pembayaran_air: string | number;
  is_valid?: boolean;
  catatan_verifikasi?: string;
};

// --- API Calls ---
const patchDataValidate = async (form: ValidationState) => {
  const response = await api.patch(
    `/verifikator/peserta/data-aset/validate`,
    form,
  );
  return response.data;
};

const patchDataReset = async ({ id_data_aset }: { id_data_aset: string }) => {
  const response = await api.patch(`/verifikator/peserta/data-aset/reset`, {
    id_data_aset,
  });
  return response.data;
};

// --- Helper Component: DataRow ---
const DataRow = ({
  label,
  value,
  highlight = false,
  icon = null,
}: {
  label: string;
  value: React.ReactNode;
  highlight?: boolean;
  icon?: React.ReactNode;
}) => (
  <div className="flex flex-col sm:flex-row sm:items-start py-3 border-b border-slate-100 dark:border-slate-800/60 last:border-0">
    <span className="text-sm font-medium text-slate-500 dark:text-slate-400 sm:w-1/3 shrink-0 flex items-center gap-2">
      {icon && (
        <span className="text-slate-400 dark:text-slate-500">{icon}</span>
      )}
      {label}
    </span>
    <span
      className={`text-sm mt-1 sm:mt-0 ${highlight ? "font-bold text-primary" : "font-medium text-slate-900 dark:text-slate-100"}`}
    >
      {value || "-"}
    </span>
  </div>
);

export default function Aset({
  dataAset,
  isPending,
}: {
  dataAset: DataAset[] | undefined;
  isPending: boolean;
}) {
  const [openTeganganListrik, setOpenTeganganListrik] = useState(false);
  const [openSumberAir, setOpenSumberAir] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [selectedDataAset, setSelectedDataAset] = useState<DataAset | null>(
    null,
  );

  const [form, setForm] = useState<ValidationState>({
    id_ref_aset_luas_tanah: "",
    aset_luas_tanah: "0",
    aset_luas_bangunan: "0",
    id_ref_aset_tegangan_listrik: "",
    id_ref_aset_pembayaran_air: "",
    id_ref_aset_sumber_air: "",
    pembayaran_air: "0",
    catatan_verifikasi: "",
  });

  const { data: dataTeganganListrik } = useQuery({
    queryKey: ["listTeganganListrikAset"],
    queryFn: getListTeganganListrikAset,
    select: (data) =>
      data?.map((teganganListrik: TeganganListrikAset) => ({
        label: `${teganganListrik.tegangan_listrik} VA`,
        value: `${teganganListrik.id_ref_aset_tegangan_listrik}`,
      })),
  });

  const { data: dataSumberAir } = useQuery({
    queryKey: ["listSumberAir"],
    queryFn: () => getListSumberAir(),
    select: (data) =>
      data?.map((sumberAir: SumberAir) => ({
        label: `${sumberAir.sumber_air}`,
        value: `${sumberAir.id_ref_aset_sumber_air}`,
      })),
  });

  const queryClient = useQueryClient();

  const {
    mutate,
    isPending: isPendingValidate,
    error,
  } = useMutation({
    mutationFn: patchDataValidate,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["peserta"] });
      setShowInput(false);
      toast.success(data?.message || "Berhasil memvalidasi data aset.");
      setForm((prev) => ({
        ...prev,
        id_ref_aset_luas_tanah: "",
        aset_luas_tanah: "0",
        aset_luas_bangunan: "0",
        id_ref_aset_tegangan_listrik: "",
        id_ref_aset_pembayaran_air: "",
        id_ref_aset_sumber_air: "",
        pembayaran_air: "0",
        catatan_verifikasi: "",
      }));
    },
    onError: (error: any) =>
      toast.error(error.response?.data?.message || "Terjadi kesalahan."),
  });

  const { mutate: mutateResetValidation, isPending: isPendingResetValidation } =
    useMutation({
      mutationFn: patchDataReset,
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["peserta"] });
        toast.success(data?.message || "Reset validasi aset berhasil.");
      },
      onError: (error: any) =>
        toast.error(
          error?.response?.data?.message || "Terjadi kesalahan saat reset.",
        ),
    });

  const handleValidate = (id_data_aset: string, is_valid: boolean) =>
    mutate({ ...form, id_data_aset, is_valid });
  const handleReset = (id_data_aset: string) =>
    mutateResetValidation({ id_data_aset });

  if (isPending)
    return (
      <div className="grid xl:grid-cols-2 grid-cols-1 gap-6">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );

  return (
    <div className="flex flex-col gap-8 w-full">
      {/* INFO ALERT */}
      <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900/50">
        <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500" />
        <AlertTitle className="text-amber-800 dark:text-amber-400 font-bold ml-2">
          Pemeriksaan Data Aset
        </AlertTitle>
        <AlertDescription className="text-amber-700/80 dark:text-amber-500/80 ml-2 mt-2">
          <p className="mb-2">
            Mohon verifikasi kesesuaian inputan peserta dengan bukti fisik:
          </p>
          <ul className="list-disc list-inside space-y-1 font-medium">
            <li>
              Kesesuaian{" "}
              <strong className="text-amber-800 dark:text-amber-300">
                Tegangan Listrik
              </strong>{" "}
              dengan struk/tagihan.
            </li>
            <li>
              Kesesuaian{" "}
              <strong className="text-amber-800 dark:text-amber-300">
                Sumber & Pembayaran Air
              </strong>{" "}
              dengan tagihan air.
            </li>
            <li>
              Kesesuaian{" "}
              <strong className="text-amber-800 dark:text-amber-300">
                Luas Tanah & Bangunan
              </strong>{" "}
              dengan PBB.
            </li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* --- LOOP DATA ASET --- */}
      {dataAset && dataAset.length > 0 ? (
        dataAset.map((item: DataAset, index: number) => {
          // Ekstrak file PBB
          const urlBerkasPbb = item.path_berkas_pbb;
          const extensionBerkasPbb = urlBerkasPbb
            ?.split(".")
            .pop()
            ?.toLowerCase();

          // Ekstrak file Air
          const urlBerkasAir = item.path_berkas_air;
          const extensionBerkasAir = urlBerkasAir
            ?.split(".")
            .pop()
            ?.toLowerCase();

          // Ekstrak file Listrik
          const urlBerkasListrik = item.path_berkas_listrik;
          const extensionBerkasListrik = urlBerkasListrik
            ?.split(".")
            .pop()
            ?.toLowerCase();

          const isValid = item.is_valid;
          const isPendingItemAction =
            isPendingResetValidation || isPendingValidate;

          return (
            <Card
              key={item.id_data_aset}
              className="overflow-hidden border-slate-200 dark:border-slate-800 shadow-sm rounded-2xl"
            >
              <div className="grid xl:grid-cols-2 grid-cols-1 divide-y xl:divide-y-0 xl:divide-x divide-slate-100 dark:divide-slate-800">
                {/* --- KIRI: AREA DOKUMEN --- */}
                <div className="flex flex-col bg-slate-50/50 dark:bg-slate-900/20">
                  <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400 rounded-lg">
                        <FileText size={20} />
                      </div>
                      <div>
                        <h2 className="font-bold text-slate-800 dark:text-slate-200 text-base">
                          Dokumen Bukti Aset
                        </h2>
                        <p className="text-xs text-slate-500">
                          Listrik, Air & PBB
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Dokumen Viewer Scrollable */}
                  <div className="flex-1 overflow-y-auto max-h-[800px] p-4 space-y-6 custom-scrollbar bg-slate-100/50 dark:bg-slate-950/50">
                    {/* Berkas Listrik */}
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold flex items-center gap-2 text-slate-700 dark:text-slate-300">
                        <Zap className="h-4 w-4 text-amber-500" /> Struk / Bukti
                        Tagihan Listrik
                      </h3>
                      <div className="relative h-[300px] sm:h-[400px] w-full bg-white dark:bg-slate-900 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm">
                        {extensionBerkasListrik === "pdf" ? (
                          <iframe
                            src={item?.path_berkas_listrik || ""}
                            title="Berkas Listrik"
                            className="absolute inset-0 h-full w-full"
                          />
                        ) : item.path_berkas_listrik ? (
                          <ImageControl imageUrl={item.path_berkas_listrik} />
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full text-slate-400">
                            <FileText size={32} className="mb-2 opacity-50" />
                            <p className="text-xs">Tidak ada berkas listrik</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Berkas Air */}
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold flex items-center gap-2 text-slate-700 dark:text-slate-300">
                        <Droplets className="h-4 w-4 text-blue-500" /> Struk /
                        Bukti Tagihan Air
                      </h3>
                      <div className="relative h-[300px] sm:h-[400px] w-full bg-white dark:bg-slate-900 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm">
                        {extensionBerkasAir === "pdf" ? (
                          <iframe
                            src={item?.path_berkas_air || ""}
                            title="Berkas Air"
                            className="absolute inset-0 h-full w-full"
                          />
                        ) : item.path_berkas_air ? (
                          <ImageControl imageUrl={item.path_berkas_air} />
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full text-slate-400">
                            <FileText size={32} className="mb-2 opacity-50" />
                            <p className="text-xs">Tidak ada berkas air</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Berkas PBB */}
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold flex items-center gap-2 text-slate-700 dark:text-slate-300">
                        <Home className="h-4 w-4 text-emerald-500" /> SPPT PBB
                      </h3>
                      <div className="relative h-[300px] sm:h-[400px] w-full bg-white dark:bg-slate-900 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm">
                        {extensionBerkasPbb === "pdf" ? (
                          <iframe
                            src={item?.path_berkas_pbb || ""}
                            title="Berkas PBB"
                            className="absolute inset-0 h-full w-full"
                          />
                        ) : item.path_berkas_pbb ? (
                          <ImageControl imageUrl={item.path_berkas_pbb} />
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full text-slate-400">
                            <FileText size={32} className="mb-2 opacity-50" />
                            <p className="text-xs">Tidak ada berkas PBB</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* --- KANAN: DATA INPUTAN PESERTA & AKSI --- */}
                <div className="flex flex-col bg-white dark:bg-slate-900 h-full">
                  {/* Header Data Kanan */}
                  <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/20">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary/10 text-primary font-bold text-sm border-2 border-white dark:border-slate-800 shadow-sm">
                        {index + 1}
                      </div>
                      <div>
                        <h2 className="font-bold text-lg text-slate-800 dark:text-slate-100 uppercase flex items-center gap-2">
                          {item.jenis_aset?.nama_jenis_aset}
                          <Badge
                            variant="outline"
                            className="text-xs font-medium dark:border-slate-700"
                          >
                            {item.kepemilikan_aset?.nama_kepemilikan}
                          </Badge>
                        </h2>
                        {isValid === true && (
                          <Badge variant="success" className="mt-1 shadow-none">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Valid
                          </Badge>
                        )}
                        {isValid === false && (
                          <Badge
                            variant="destructive"
                            className="mt-1 shadow-none"
                          >
                            <XCircle className="w-3 h-3 mr-1" /> Tidak Valid
                          </Badge>
                        )}
                        {isValid === null && (
                          <Badge
                            variant="secondary"
                            className="mt-1 shadow-none bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                          >
                            Belum Diperiksa
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* List Data Inputan */}
                  <div className="p-6 flex-1 overflow-y-auto custom-scrollbar">
                    {/* Section Listrik */}
                    <div className="mb-6">
                      <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 border-b border-slate-200 dark:border-slate-800 pb-2 mb-2 flex items-center gap-2">
                        <Zap className="h-4 w-4 text-amber-500" /> Informasi
                        Listrik
                      </h3>
                      <DataRow
                        label="Tegangan Listrik"
                        value={`${item.tegangan_listrik?.tegangan_listrik || 0} VA`}
                        highlight
                      />
                    </div>

                    {/* Section Air */}
                    <div className="mb-6">
                      <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 border-b border-slate-200 dark:border-slate-800 pb-2 mb-2 flex items-center gap-2">
                        <Droplets className="h-4 w-4 text-blue-500" /> Informasi
                        Air
                      </h3>
                      <DataRow
                        label="Sumber Air"
                        value={item.sumber_air?.sumber_air}
                      />
                      <DataRow
                        label="Rentang Pembayaran Air"
                        value={
                          item.ref_pembayaran_air?.aset_pembayaran_air_text
                        }
                      />
                      <DataRow
                        label="Nominal Inputan / Bulan"
                        value={
                          item.pembayaran_air
                            ? formatRupiah(Number(item.pembayaran_air))
                            : "-"
                        }
                        highlight
                      />
                    </div>

                    {/* Section Bangunan/PBB */}
                    <div className="mb-6">
                      <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 border-b border-slate-200 dark:border-slate-800 pb-2 mb-2 flex items-center gap-2">
                        <Home className="h-4 w-4 text-emerald-500" /> Informasi
                        Luas (PBB)
                      </h3>
                      <DataRow
                        label="Luas Tanah"
                        value={`${item.aset_luas_tanah || 0} m²`}
                        highlight
                      />
                      <DataRow
                        label="Luas Bangunan"
                        value={`${item.aset_luas_bangunan || 0} m²`}
                        highlight
                      />
                    </div>

                    {/* Catatan Verifikator */}
                    {item.catatan_verifikasi && (
                      <div className="mt-4 p-4 bg-red-50 dark:bg-red-950/30 rounded-xl border border-red-100 dark:border-red-900/50">
                        <p className="text-xs font-semibold text-red-800 dark:text-red-400 mb-1">
                          Catatan Koreksi Verifikator:
                        </p>
                        <p className="text-sm text-red-700 dark:text-red-300 italic">
                          "{item.catatan_verifikasi}"
                        </p>
                      </div>
                    )}
                  </div>

                  {/* ACTION BUTTONS BAWAH */}
                  <div className="p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex flex-col sm:flex-row justify-end gap-3 mt-auto">
                    {isValid !== null ? (
                      <Button
                        variant="outline"
                        onClick={() => handleReset(item.id_data_aset)}
                        disabled={isPendingItemAction}
                        className="w-full sm:w-auto dark:border-slate-700"
                      >
                        {isPendingResetValidation ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <GiBackwardTime className="mr-2 h-4 w-4" />
                        )}
                        Reset Hasil Validasi
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="destructive"
                          disabled={isPendingItemAction}
                          className="w-full sm:w-auto"
                          onClick={() => {
                            setShowInput(true);
                            setSelectedDataAset(item);
                            setForm((prev) => ({
                              ...prev,
                              id_ref_aset_tegangan_listrik:
                                item.id_ref_aset_tegangan_listrik || "",
                              id_ref_aset_pembayaran_air:
                                item.id_ref_aset_pembayaran_air || "",
                              pembayaran_air: item.pembayaran_air || "0",
                              id_ref_aset_luas_tanah:
                                item.id_ref_aset_luas_tanah || "",
                              aset_luas_tanah: item.aset_luas_tanah || "0",
                              aset_luas_bangunan:
                                item.aset_luas_bangunan || "0",
                              id_ref_aset_sumber_air:
                                item?.id_ref_aset_sumber_air || "",
                            }));
                          }}
                        >
                          <XCircle className="w-4 h-4 mr-2" /> Tidak Valid
                        </Button>
                        <Button
                          className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white"
                          disabled={isPendingItemAction}
                          onClick={() =>
                            handleValidate(item.id_data_aset, true)
                          }
                        >
                          {isPendingValidate ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                          )}
                          Valid (Sesuai Bukti)
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })
      ) : (
        <div className="p-10 text-center bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
          <Home className="mx-auto h-12 w-12 text-slate-400 mb-4" />
          <p className="text-slate-500 font-medium">
            Data Aset Tidak Ditemukan
          </p>
        </div>
      )}

      {/* ================= MODAL KOREKSI ASET ================= */}
      <Modal
        show={showInput}
        onClose={() => setShowInput(false)}
        className="lg:w-[32rem]"
      >
        <form
          className="flex flex-col gap-y-5 p-1"
          onSubmit={(e) => {
            e.preventDefault();
            handleValidate(selectedDataAset?.id_data_aset || "", false);
          }}
        >
          <FormHeader
            title={`Koreksi Data ${selectedDataAset?.jenis_aset?.nama_jenis_aset || "Aset"}`}
            description="Sesuaikan nilai dengan dokumen pendukung (Listrik, Air, PBB)."
            icon={<MdModeEdit className="text-primary h-6 w-6" />}
          />

          <div className="space-y-4 max-h-[60vh] overflow-y-auto px-1 custom-scrollbar">
            {/* Bagian Listrik */}
            <div className="space-y-3 p-4 bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 rounded-xl">
              <h4 className="font-semibold text-sm flex items-center gap-2 text-slate-700 dark:text-slate-300">
                <Zap className="w-4 h-4 text-amber-500" /> Data Listrik
              </h4>
              <div>
                <Label className="text-slate-600 dark:text-slate-400">
                  Tegangan Listrik Sebenarnya
                </Label>
                <Combobox
                  open={openTeganganListrik}
                  setOpen={setOpenTeganganListrik}
                  value={form.id_ref_aset_tegangan_listrik || ""}
                  setValue={(v) =>
                    setForm((p) => ({ ...p, id_ref_aset_tegangan_listrik: v }))
                  }
                  options={dataTeganganListrik}
                  placeholder="Pilih tegangan listrik"
                />
                <InputError
                  text={error?.response?.data?.errors?.id_ref_aset_tegangan_listrik?.join(
                    ",",
                  )}
                />
              </div>
            </div>

            {/* Bagian Air */}
            <div className="space-y-3 p-4 bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 rounded-xl">
              <h4 className="font-semibold text-sm flex items-center gap-2 text-slate-700 dark:text-slate-300">
                <Droplets className="w-4 h-4 text-blue-500" /> Data Air
              </h4>
              <div>
                <Label required className="text-slate-600 dark:text-slate-400">
                  Sumber Air Sebenarnya
                </Label>
                <Combobox
                  open={openSumberAir}
                  setOpen={setOpenSumberAir}
                  value={form.id_ref_aset_sumber_air || ""}
                  setValue={(v) =>
                    setForm((p) => ({ ...p, id_ref_aset_sumber_air: v }))
                  }
                  options={dataSumberAir}
                  placeholder="Pilih sumber air"
                />
                <InputError
                  text={error?.response?.data?.errors?.id_ref_aset_sumber_air?.join(
                    ",",
                  )}
                />
              </div>
              <div>
                <Label className="text-slate-600 dark:text-slate-400">
                  Nominal Pembayaran Air / Bulan
                </Label>
                <Input
                  placeholder="Misal: 50000"
                  value={formatRupiah(Number(form.pembayaran_air))}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      pembayaran_air: e.target.value.replace(/\D/g, ""),
                    }))
                  }
                  className="font-mono focus-visible:ring-primary"
                />
                <InputError
                  text={error?.response?.data?.errors?.pembayaran_air?.join(
                    ",",
                  )}
                />
              </div>
            </div>

            {/* Bagian PBB */}
            <div className="space-y-3 p-4 bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 rounded-xl">
              <h4 className="font-semibold text-sm flex items-center gap-2 text-slate-700 dark:text-slate-300">
                <Home className="w-4 h-4 text-emerald-500" /> Data Tanah &
                Bangunan
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label
                    required
                    className="text-slate-600 dark:text-slate-400"
                  >
                    Luas Tanah (m²)
                  </Label>
                  <Input
                    required
                    placeholder="Misal: 100"
                    value={String(form.aset_luas_tanah)}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        aset_luas_tanah: Number(
                          e.target.value.replace(/\D/g, ""),
                        ),
                      }))
                    }
                    className="font-mono focus-visible:ring-primary"
                  />
                </div>
                <div>
                  <Label
                    required
                    className="text-slate-600 dark:text-slate-400"
                  >
                    Luas Bangunan (m²)
                  </Label>
                  <Input
                    required
                    placeholder="Misal: 45"
                    value={String(form.aset_luas_bangunan)}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        aset_luas_bangunan: Number(
                          e.target.value.replace(/\D/g, ""),
                        ),
                      }))
                    }
                    className="font-mono focus-visible:ring-primary"
                  />
                </div>
              </div>
            </div>

            {/* Catatan */}
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">
                Catatan Verifikator <span className="text-red-500">*</span>
              </Label>
              <Textarea
                placeholder="Sebutkan detail ketidaksesuaian data..."
                value={form.catatan_verifikasi}
                onChange={(e) =>
                  setForm((p) => ({ ...p, catatan_verifikasi: e.target.value }))
                }
                rows={3}
                className="focus-visible:ring-primary"
              />
              <InputError
                text={error?.response?.data?.errors?.catatan_verifikasi?.join(
                  ",",
                )}
              />
            </div>
          </div>

          {/* Action Modal */}
          <div className="mt-2 flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowInput(false)}
              disabled={isPendingValidate}
              className="dark:border-slate-700"
            >
              Batal
            </Button>
            <Button
              type="submit"
              disabled={isPendingValidate}
              variant="destructive"
            >
              {isPendingValidate ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                "Simpan Koreksi"
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
